export const CUSTOMER_ACTIVITY_PRESENTATION = {
  svr: { label: "SVR", description: "Site visit created", className: "bg-blue-100 text-blue-700" },
  ro: { label: "RO", description: "Repair order created", className: "bg-amber-100 text-amber-700" },
  purchase: { label: "Purchased", description: "Purchase recorded", className: "bg-emerald-100 text-emerald-800" },
  payment: { label: "Paid", description: "Payment received (full or partial)", className: "bg-green-100 text-green-800" },
  proposal: { label: "Approved", description: "Proposal accepted or approved", className: "bg-violet-100 text-violet-800" },
  warranty: { label: "Warranty", description: "Warranty card created", className: "bg-cyan-100 text-cyan-800" },
} as const;

export type CustomerActivityType = keyof typeof CUSTOMER_ACTIVITY_PRESENTATION;
export type CustomerActivity = {
  customerId: number | null;
  activityType: CustomerActivityType;
  activityDate: Date | string | null;
};
type DirectoryCustomer = {
  id: number;
  name: string;
  hiddenFromList: boolean;
  mergedIntoCustomerId: number | null;
};

/** Resolve identity and deduplicate BEFORE limiting, so merged/hidden records do not consume slots. */
export function rankRecentCustomers<T extends DirectoryCustomer>(
  customers: T[], activities: CustomerActivity[], limit: number,
) {
  const directory = new Map(customers.map(customer => [customer.id, customer]));
  const latest = new Map<number, { customer: T; activityType: CustomerActivityType; activityDate: Date }>();
  for (const activity of activities) {
    if (activity.customerId == null || !activity.activityDate) continue;
    const activityDate = new Date(activity.activityDate);
    if (!Number.isFinite(activityDate.getTime())) continue;
    let customer = directory.get(activity.customerId);
    const visited = new Set<number>();
    while (customer?.mergedIntoCustomerId != null && !visited.has(customer.id)) {
      visited.add(customer.id);
      customer = directory.get(customer.mergedIntoCustomerId);
    }
    if (!customer || customer.hiddenFromList || customer.mergedIntoCustomerId != null) continue;
    const previous = latest.get(customer.id);
    if (!previous || activityDate > previous.activityDate ||
        (activityDate.getTime() === previous.activityDate.getTime() && activity.activityType < previous.activityType)) {
      latest.set(customer.id, { customer, activityType: activity.activityType, activityDate });
    }
  }
  return Array.from(latest.values())
    .sort((a, b) => b.activityDate.getTime() - a.activityDate.getTime() || a.customer.id - b.customer.id)
    .slice(0, limit)
    .map(({ customer, ...activity }) => {
      const { hiddenFromList, mergedIntoCustomerId, ...identity } = customer;
      return { ...identity, ...activity };
    });
}

/** Partial payment history is event data; sync/update timestamps are not payment dates. */
export function paymentActivityDates(invoice: {
  invoiceStatus: string;
  paidAt: Date | string | null;
  partialPaymentsJson?: unknown;
}): Array<Date | string> {
  if (!["paid", "partial"].includes(invoice.invoiceStatus)) return [];
  const dates: Array<Date | string> = [];
  if (invoice.invoiceStatus === "paid" && invoice.paidAt) dates.push(invoice.paidAt);
  if (Array.isArray(invoice.partialPaymentsJson)) {
    for (const event of invoice.partialPaymentsJson) {
      if (event && Number(event.amount) > 0 && typeof event.paidAt === "string") dates.push(event.paidAt);
    }
  }
  return dates;
}
